import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";

import { PROPERTIES_URL } from "../../config";
import { PropertiesApiResponse } from "../../types";

/**
 * Tope de espera del catálogo. Pesa unos 7 KB comprimido: si en 20 s no llegó,
 * no va a llegar, y «Ingresar» —que lo espera— tiene que dejar de esperar y
 * decir qué pasó en vez de quedarse deshabilitado (VWA-43).
 */
const PROPERTIES_TIMEOUT_MS = 20000;

const rawBaseQuery = fetchBaseQuery();

/**
 * `fetchBaseQuery({ timeout })` no alcanza: en RTK 1.9.7 apaga su temporizador
 * en cuanto llegan los encabezados, y un cuerpo que no termina de llegar deja
 * la consulta colgada. Este tope cubre la petición entera.
 */
const baseQueryWithTimeout: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const timer = setTimeout(() => api.abort(), PROPERTIES_TIMEOUT_MS);
  try {
    return await rawBaseQuery(args, api, extraOptions);
  } finally {
    clearTimeout(timer);
  }
};

export const propertiesApi = createApi({
  reducerPath: "propertiesApi",
  baseQuery: baseQueryWithTimeout,
  endpoints: (builder) => ({
    getProperties: builder.query<PropertiesApiResponse, string>({
      query: (url) => {
        return {
          url,
          method: "GET",
        };
      },
    }),
  }),
});

export const { useLazyGetPropertiesQuery, useGetPropertiesQuery } =
  propertiesApi;

/**
 * En qué quedó la descarga del catálogo a `PROPERTIES_URL`, según RTK Query.
 * Lo consulta «Ingresar» para esperarlo y para decidir si reintentar.
 */
export const selectPropertiesDownload =
  propertiesApi.endpoints.getProperties.select(PROPERTIES_URL);

/**
 * Si una respuesta es de verdad el catálogo. El Java responde 200 aunque
 * rechace, y una respuesta que no es el catálogo —una página de error, un
 * cuerpo vacío— no trae `result`.
 *
 * @param response La respuesta de `getProperties`, si la hubo.
 * @returns `true` solo si es un catálogo aceptado (`responseCode` 200).
 */
export const isValidCatalog = (
  response: PropertiesApiResponse | null | undefined,
): response is PropertiesApiResponse => response?.result?.responseCode === 200;
